import React, { useEffect, useState, useRef } from 'react'
import { useParams, useHistory, useLocation } from 'react-router'
import {
  Modal as ModalAntd,
  Radio as RadioAntd,
  Button as ButtonAntd,
  Card,
  AutoComplete,
  Form,
  Select,
  DatePicker,
  Skeleton,
} from 'antd'
import {
  format,
  differenceInMinutes,
  parseISO,
  startOfToday,
  startOfDay,
  formatDistanceToNow,
} from 'date-fns'
import { ptBR } from 'date-fns/locale'
import dayjs from 'dayjs'
import 'dayjs/locale/pt-br'
import weekday from 'dayjs/plugin/weekday'
import localeData from 'dayjs/plugin/localeData'
import locale from 'antd/es/date-picker/locale/pt_BR'

// Configure dayjs with required plugins for Ant Design
dayjs.extend(weekday)
dayjs.extend(localeData)
dayjs.locale('pt-br')
import { NavEstrutural } from '../../components/NavEstrutural'
import { Show } from '../../components/Basic'
import { Avatar } from '../../components/Avatar'
import { DisplayFlex } from '../../components/Styles'
import { useDecorateBlobWithSas } from '../../components/UseDecorateBlobWithSas'
import styled from '@emotion/styled'
import { toast } from 'react-toastify'
import { copyToClipboard } from '../../util/copyToClipboard'
import { ProgressoStore } from './ProgressoStore'
import { TextFieldInput } from '../../components/TextField'
import Inputmask from 'inputmask'
import { MatriculaModel, STATUS_MATRICULA } from '../../models/MatriculaModel'
import iconSearch from '../../assets/search.svg'
import { encodeHashCobranca } from '@hyperdev-tech/hash-cobranca'
import api from '../../api'
import { ConfirmAlert } from '../../components/ConfirmAlert'
import { CURSOS } from '../../models/CursoModel'
import {
  Segment,
  Tab,
  Button,
  Icon,
  Modal,
  Checkbox,
} from '../../components/semantic-ui-antd-adapter'
import { Table } from 'antd'
import { CopyFilled } from '@ant-design/icons'
import { CopyElement } from '../../components/CopyElement'

interface ProgressoParams {
  cursoId
  usuarioId
}

export function Progresso() {
  const [activeTabIndex, setActiveTabIndex] = useState(0)

  const handleTabChange = (event: any, data: any) => {
    setActiveTabIndex(data.activeIndex)
  }

  return (
    <ProgressoStore.Provider>
      <Init />
      <ProgressoContent activeTabIndex={activeTabIndex} handleTabChange={handleTabChange} />
    </ProgressoStore.Provider>
  )
}

const ProgressoContent = ({ activeTabIndex, handleTabChange }) => {
  return (
    <div className="ui container">
      <BotaoVoltar />
      <Segment color="purple" style={{ marginTop: 25 }}>
        <IdentificacaoAluno />
      </Segment>
      <Segment color="orange" style={{ marginTop: 25, marginBottom: 40 }}>
        <h2>Acompanhamento de progresso do aluno</h2>
        <Tab
          activeIndex={activeTabIndex}
          onTabChange={handleTabChange}
          panes={[
            { menuItem: 'Estatisticas de estudo', render: () => <EstatisticasDeEstudo /> },
            { menuItem: 'Sessões de estudo', render: () => <Sessoes /> },
            { menuItem: 'Acompanhamento', render: () => <Acompanhamento /> },
            { menuItem: 'Validações Faciais', render: () => <ValidacoesFaciais /> },
            { menuItem: 'Avaliações', render: () => <Avaliacoes /> },
            { menuItem: 'Outros Cursos', render: () => <OutrosCursos /> },
          ]}
        />
      </Segment>
    </div>
  )
}

const Init = () => {
  const { dadosAluno } = ProgressoStore.useState()
  const dispatch = ProgressoStore.useDispatch()
  const { usuarioId } = useParams<ProgressoParams>()
  const queryParams = new URLSearchParams(useLocation().search)
  const matriculaId = Number(queryParams.get('matriculaId'))

  useEffect(() => {
    dispatch(ProgressoStore.thunks.getDados(usuarioId, matriculaId))
    dispatch(ProgressoStore.thunks.getEstados())
  }, [usuarioId, matriculaId])

  useEffect(() => {
    if (!dadosAluno?.estado) return
    dispatch(ProgressoStore.thunks.getMunicipios({ estados: dadosAluno.estado }))
  }, [dadosAluno?.estado])

  return null
}

const DadosNotaFiscal = () => {
  const { Container } = DadosNotaFiscal
  const { matricula } = ProgressoStore.useState()
  const [notaFiscal, setNotaFiscal] = useState<any>(null)
  const [nonce, setNonce] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!matricula?.id) return
    api.NotasFiscais.getByMatricula(matricula.id)
      .then(setNotaFiscal)
      .finally(() => setLoading(false))
  }, [matricula?.id])

  useEffect(() => {
    if (!notaFiscal?.id) return
    api.NotasFiscais.getNonce(notaFiscal.id).then((r) => setNonce(r.nonce))
  }, [notaFiscal?.id])

  const handleDownload = () => {
    if (!notaFiscal?.id || !nonce) return
    const url = `${import.meta.env.VITE_API_URL || 'http://localhost:3503'}/notas-fiscais/download/${notaFiscal.id}/${nonce}`
    window.open(url, '_blank')
  }

  if (loading) return <Skeleton.Input active style={{ width: '100%', marginTop: 16 }} />

  if (!notaFiscal) return null

  return (
    <Container>
      <ButtonAntd type="primary" onClick={handleDownload} disabled={!nonce}>
        Download PDF da Nota Fiscal
      </ButtonAntd>
    </Container>
  )
}
DadosNotaFiscal.Container = styled.div`
  margin-top: 16px;
`

const IdentificacaoAluno = () => {
  const { DadosAlunoContainer, DadosMatriculaContainer, DadosCobrancaContainer, Container } =
    IdentificacaoAluno
  const { dadosAluno, matricula, loading } = ProgressoStore.useState()
  const dispatch = ProgressoStore.useDispatch()
  const [editing, setEditing] = useState(false)

  const updateDadosAluno = (e) => {
    dispatch(ProgressoStore.actions.updateDadosAluno({ [e.target.name]: e.target.value }))
  }

  const saveChanges = () => {
    dispatch(ProgressoStore.thunks.updateDadosAluno())
    setEditing(false)
  }

  const cancelChanges = () => {
    dispatch(ProgressoStore.thunks.getDadosAluno())
    setEditing(false)
  }

  const urlCert = matricula?.identificacaoCertificado
    ? `https://app.newdriver.com.br/certificado/${matricula?.identificacaoCertificado}`
    : ''

  return (
    <div>
      <Container>
        <div>
          <DadosAlunoContainer>
            <div className="entire-col">
              <DisplayFlex verticalCenter>
                {dadosAluno ? <Avatar usuario={dadosAluno} circular={true} /> : <Skeleton.Avatar />}
              </DisplayFlex>
            </div>
            <TextFieldInput
              label="Aluno Id"
              disabled={true}
              value={dadosAluno?.id}
              loading={loading}
            />
            <TextFieldInput
              name="nome"
              label="Nome"
              disabled={!editing}
              value={dadosAluno?.nome}
              onChange={updateDadosAluno}
              loading={loading}
            />
            <TextFieldInput
              mask="cpf"
              name="cpf"
              label="CPF"
              disabled={true}
              value={dadosAluno?.cpf}
              loading={loading}
              showCopyButton={true}
            />
            <TextFieldInput
              name="telefone"
              label="Telefone"
              mask="telefone"
              disabled={!editing}
              value={dadosAluno?.telefone}
              onChange={updateDadosAluno}
              loading={loading}
            />
            <TextFieldInput
              name="email"
              label="E-mail"
              disabled={!editing}
              value={dadosAluno?.email}
              onChange={updateDadosAluno}
              loading={loading}
            />
            <DataNascimentoPicker disabled={!editing} />
            <TextFieldInput
              name="cnh"
              label="Número da CNH"
              mask="999999999-99"
              disabled={!editing}
              value={dadosAluno?.cnh}
              onChange={updateDadosAluno}
              className="entire-col"
              loading={loading}
            />
            <TextFieldInput
              name="renach"
              label="Renach"
              disabled={!editing}
              value={dadosAluno?.renach ?? matricula?.renach}
              onChange={updateDadosAluno}
              className="entire-col"
              loading={loading}
            />
            <DataPrimeiraHabilitacaoPicker disabled={!editing} />
            <CategoriaCNHSelect disabled={!editing} />
            <ComboBoxEstados disabled={!editing} />
            <ComboBoxMunicipios disabled={!editing} />
            <Checkbox
              label={'Ignorar Validação Serpro'}
              disabled={!editing}
              checked={!!dadosAluno?.ignorarValidacaoSerpro}
              onChange={(e) => {
                dispatch(
                  ProgressoStore.actions.updateDadosAluno({
                    ignorarValidacaoSerpro: !dadosAluno?.ignorarValidacaoSerpro,
                  })
                )
              }}
              className="entire-col"
            />
            <div
              className="entire-col"
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
                alignItems: 'flex-start',
              }}
            >
              {editing ? (
                <div>
                  <Button className="entire-col" onClick={saveChanges}>
                    Salvar
                  </Button>
                  <Button className="entire-col" onClick={cancelChanges}>
                    Cancelar
                  </Button>
                </div>
              ) : (
                <Button className="entire-col" onClick={() => setEditing(true)}>
                  Editar
                </Button>
              )}
              <GerarNovoToken />
              <LinksDetran />
              <AcoesDetran />
            </div>
          </DadosAlunoContainer>
          <DadosNotaFiscal />
        </div>

        <div>
          <Avisos />
          <DadosMatriculaContainer>
            <Label
              label="Matricula ID"
              value={<CopyElement value={String(matricula?.id)}>{matricula?.id}</CopyElement>}
            />
            <Label label="Curso" value={matricula?.curso?.nomeReduzido} />
            <Label label="UF" value={matricula?.uf} />
            <Label label="Status Detran" value={matricula?.statusDetran.replace(/_/g, ' ')} />
            {matricula?.turmaId && <Label label="Turma ID" value={matricula?.turmaId} />}
            {matricula?.parceiro && <Label label="Parceiro" value={matricula?.parceiro?.nome} />}
            {matricula?.parceiro && (
              <Label label="Parceiro CPF/CNPJ" value={matricula?.parceiro?.cpfCnpj} />
            )}
            {matricula?.parceiro?.parceiroPai && (
              <Label label="Parceiro Pai" value={matricula?.parceiro?.parceiroPai?.nome} />
            )}
            {matricula?.parceiro?.parceiroPai && (
              <Label
                label="Parceiro Pai CPF/CNPJ"
                value={matricula?.parceiro?.parceiroPai?.cpfCnpj}
              />
            )}
            <Label label="Data do Início do Curso" value={formatDate(matricula?.dataInicioCurso)} />
            <Label label="Data do Fim do Curso" value={formatDate(matricula?.dataFimCurso)} />
            <Label
              label="Data do Início do Módulo Teórico"
              value={formatDate(matricula?.dataInicioModuloTeorico)}
            />
            <Label
              label="Data do Fim do Módulo Teórico"
              value={formatDate(matricula?.dataFimModuloTeorico)}
            />
            <Label
              label="Data da Validação Inicial"
              value={formatDate(matricula?.dataValidacaoFacialInicial)}
            />
            <Label
              label="Data da Validação Final"
              value={formatDate(matricula?.dataValidacaoFacialFinal)}
            />
            <Label label="Nota" value={matricula?.nota} className="entire-col" />
            <Label
              label="Identificação Certificado"
              value={matricula?.identificacaoCertificadoRetornado}
              className="entire-col"
            />
            {matricula?.identificacaoCertificadoRetornadoTeorico && (
              <Label
                label="Identificação Certificado Teórico"
                value={matricula?.identificacaoCertificadoRetornadoTeorico}
                className="entire-col"
              />
            )}
            <Label
              label="Certificado"
              value={
                <a target="_blank" rel="noreferrer" href={urlCert}>
                  {urlCert}
                </a>
              }
              className="entire-col"
            />

            <PermiteEmissaoCertificado />
            <ReenviarConclusao />
          </DadosMatriculaContainer>
          <DadosCobrancaContainer>
            <InformacoesCobranca />
          </DadosCobrancaContainer>
        </div>
      </Container>
    </div>
  )
}
IdentificacaoAluno.Container = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
`
IdentificacaoAluno.DadosAlunoContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
  padding: 15px;
  background-color: #f3e4d48d;
  border-radius: 20px;
  border: 1px solid #f7c286;

  .entire-col {
    grid-column: 1 / 3;
  }
`
IdentificacaoAluno.DadosMatriculaContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
  padding: 15px;
  background-color: #f3e4d48d;
  border-radius: 20px;
  border: 1px solid #f7c286;

  .entire-col {
    grid-column: 1 / 3;
  }
`
IdentificacaoAluno.DadosCobrancaContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
  padding: 15px;
  background-color: #f3e4d48d;
  border-radius: 20px;
  border: 1px solid #f7c286;
  margin-top: 16px;

  .entire-col {
    grid-column: 1 / 3;
  }
`

const AjustesCobrancaContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 12px;
  border: 1px solid #ddb58a;
  border-radius: 8px;
`

function Avisos() {
  const { Container, Card } = Avisos
  const { matricula } = ProgressoStore.useState()
  const isCursoPraticoSemParceiro =
    [
      STATUS_MATRICULA?.AGUARDANDO_CONCLUSAO_MODULO_PRATICO,
      STATUS_MATRICULA?.AGUARDANDO_MATRICULA_MODULO_PRATICO,
    ].includes(matricula?.statusDetran) &&
    !matricula?.parceiroCursoPraticoId &&
    !matricula?.parceiroId

  return (
    <Show condition={[isCursoPraticoSemParceiro].includes(true)}>
      <Container>
        <Card
          title={<strong>Avisos</strong>}
          size="small"
          headStyle={{ color: 'red' }}
          bordered={false}
        >
          <Show condition={isCursoPraticoSemParceiro}>
            <p>
              <b>Atenção:</b> O aluno finalizou o modulo teórico e está aguardando matrícula e
              conclusão no curso prático. Informe o aluno para procurar o parceiro do curso prático.
              E fazer a busca pelo CPF do aluno no formulário de matrícula do curso prático, para
              lançar a matrícula no sistema do Detran.
            </p>
          </Show>
        </Card>
      </Container>
    </Show>
  )
}
Avisos.Container = styled.div`
  background-color: #f3e4d48d;
  border-radius: 20px;
  border: 1px solid #f7c286;
  margin-bottom: 14px;
  padding: 15px;
`
Avisos.Card = styled(Card)`
  background-color: #f8f0e7;
`

function PermiteEmissaoCertificado() {
  const dispatch = ProgressoStore.useDispatch()
  const { permiteEmissaoCertificado, pagamento, id } = ProgressoStore.useState().matricula || {}
  const { loading } = ProgressoStore.useState()
  function handlePermiteEmissaoCertificado(e: any) {
    dispatch(ProgressoStore.actions.changePermiteEmissaoCertificado(e.target.value))
  }
  function handlePermiteCertificado() {
    if (id) {
      dispatch(ProgressoStore.thunks.updatePermissaoCertificado(id, permiteEmissaoCertificado!))
    }
  }

  return (
    <div>
      Permite Emissão de Certificado?
      <RadioAntd.Group
        onChange={handlePermiteEmissaoCertificado}
        value={!!permiteEmissaoCertificado}
      >
        <RadioAntd value={true}>Permite</RadioAntd>
        <RadioAntd value={false}>Não Permite</RadioAntd>
      </RadioAntd.Group>
      <Show condition={pagamento?.referenciaPagamento}>
        <Button onClick={handlePermiteCertificado} loading={loading}>
          Salvar
        </Button>
      </Show>
    </div>
  )
}

function ReenviarConclusao() {
  const dispatch = ProgressoStore.useDispatch()
  const { loading, matricula } = ProgressoStore.useState()
  const [modulo, setModulo] = useState<'TEORICO' | 'PRATICO'>('TEORICO')
  const isCursoMoto = [
    CURSOS.MOTOFRETISTA,
    CURSOS.MOTOTAXISTA,
    CURSOS.ATUALIZACAO_MOTOFRETISTA,
    CURSOS.ATUALIZACAO_MOTOTAXISTA,
  ].includes(matricula?.cursoId)

  const handleReenviarConclusao = async () => {
    const result = await ConfirmAlert({
      title: 'Deseja reenviar a conclusão?',
      content: 'Ao reenviar a conclusão, o número do certificado será alterado!',
    })
    if (!result) return

    await dispatch(
      ProgressoStore.thunks.reenviarConclusao(matricula, isCursoMoto ? modulo : 'TEORICO')
    )
  }

  return (
    <Show condition={matricula?.statusDetran === STATUS_MATRICULA?.FINALIZADO}>
      <div className="entire-col">
        <Show condition={isCursoMoto}>
          <RadioAntd.Group onChange={(e) => setModulo(e.target.value)} value={modulo}>
            <RadioAntd value="TEORICO">Módulo Teórico</RadioAntd>
            <RadioAntd value="PRATICO">Módulo Prático</RadioAntd>
          </RadioAntd.Group>
        </Show>
        <Button onClick={handleReenviarConclusao} loading={loading}>
          Reenviar Conclusão
        </Button>
      </div>
    </Show>
  )
}

const InformacoesCobranca = () => {
  const { matricula } = ProgressoStore.useState()
  const isTurma = !!matricula?.turmaId

  if (isTurma) {
    return (
      <div className="entire-col">Aluno pertence a uma turma e não possui dados de cobrança!</div>
    )
  }
  return (
    <>
      <DadosCobranca />
      <AjustesCobranca />
      <AplicarCupom />
      <ReemitirCobranca />
      <Cupom />
      <ConfirmarPagamentoManual />
    </>
  )
}

function DadosCobranca() {
  const { matricula } = ProgressoStore.useState()
  const cobrancaData = buildCobrancaViewModel(matricula)

  return (
    <>
      <Show
        condition={
          cobrancaData.referenciaPagamento && !cobrancaData.isIsento && !cobrancaData.isMercadoPago
        }
      >
        <Label label="Referência da Cobrança" value={cobrancaData.referenciaPagamento} />
      </Show>

      <Show condition={!cobrancaData.isIsento}>
        <Label label="Status da Cobrança" value={cobrancaData.status} />
      </Show>

      <Show condition={cobrancaData.dataPagamento}>
        <Label label="Meio de Pagamento" value={cobrancaData.meioPagamento ?? '-'} />
      </Show>

      <Show condition={!cobrancaData.isIsento}>
        <Label label="Provedor de pagamento" value={cobrancaData.provedorPagamento ?? '-'} />
      </Show>

      <Show condition={cobrancaData.e2eId}>
        <Label label="E2E ID PIX" value={cobrancaData.e2eId} />
      </Show>

      <Label label="Valor" value={cobrancaData.valorFormatado} />

      <Show condition={cobrancaData.dataPagamento}>
        <Label label="Data do Pagamento" value={formatDate(cobrancaData.dataPagamento ?? '-')} />
      </Show>

      <Show
        condition={!cobrancaData.isIsento && !cobrancaData.isMercadoPago && !cobrancaData.isEfi}
      >
        <Label label="Data do Vencimento" value={formatDate(cobrancaData.dataVencimento ?? '-')} />
      </Show>

      <Show condition={cobrancaData.confirmadoPorUsuario}>
        <Label label="Confirmação por" value={cobrancaData.confirmadoPorUsuario} />
      </Show>

      <Show condition={cobrancaData.descricaoConfirmacao}>
        <Label
          label="Descrição da confirmação"
          value={cobrancaData.descricaoConfirmacao}
          className="entire-col"
        />
      </Show>

      <Show condition={cobrancaData.link}>
        <Label
          label="Link da cobrança"
          value={
            <a target="_blank" rel="noreferrer" href={cobrancaData.link}>
              {cobrancaData.link}
            </a>
          }
          className="entire-col"
        />
      </Show>
    </>
  )
}

function buildCobrancaViewModel(matricula) {
  const pagamento = matricula?.pagamento
  const cobranca = matricula?.cobranca

  const isEfi = pagamento?.provedorPagamento === 'EFI'
  const isIsento = pagamento?.meioPagamento === 'ISENTO'
  const isMercadoPago = pagamento?.provedorPagamento === 'MERCADO_PAGO'

  const infoPix = pagamento?.infoPix
  const e2eId = infoPix?.e2eId ?? null

  const referenciaPagamento = pagamento?.referenciaPagamento

  const valorCobranca = isIsento
    ? pagamento?.valorOriginal
    : (pagamento?.valor ?? pagamento?.valorOriginal)

  const valorFormatado = formatCurrency(valorCobranca)

  const dataPagamento = isEfi
    ? pagamento?.dataPagamento
    : (cobranca?.dataPagamento ?? pagamento?.dataPagamento)

  const dataVencimento = isEfi
    ? pagamento?.dataVencimento
    : (cobranca?.dataVencimento ?? pagamento?.dataVencimento)

  const status = getStatusCobranca({
    matricula,
    pagamento,
    cobranca,
    isEfi,
    isMercadoPago,
    dataVencimento,
  })

  const link = getLinkCobranca({
    pagamento,
    isEfi,
    isIsento,
    isMercadoPago,
    referenciaPagamento,
  })

  return {
    isEfi,
    isIsento,
    isMercadoPago,

    referenciaPagamento,
    valorFormatado,
    dataPagamento,
    dataVencimento,
    status,
    link,

    descricaoConfirmacao: pagamento?.descricaoConfirmacao,
    confirmadoPorUsuario: pagamento?.confirmadoPorUsuario?.nome,

    meioPagamento: pagamento?.meioPagamento,
    provedorPagamento: pagamento?.provedorPagamento,
    e2eId,
  }
}

function formatCurrency(valor) {
  const num = Number(valor)
  if (isNaN(num)) return '-'

  return num.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  })
}

function getLinkCobranca({ pagamento, isEfi, isIsento, isMercadoPago, referenciaPagamento }) {
  if (isEfi) {
    const hash = pagamento?.hashPagamento
    return hash ? `https://newdriver.com.br/pagamento/${hash}` : null
  }

  if (isIsento || isMercadoPago) return null

  return `https://conta.pilotar.app/cobranca/${encodeHashCobranca(+referenciaPagamento)}`
}

function getStatusCobranca({
  matricula,
  pagamento,
  cobranca,
  isEfi,
  isMercadoPago,
  dataVencimento,
}) {
  if (!matricula) return ''

  if (isEfi) {
    if (pagamento?.dataPagamento) return 'PAGA'
    return 'EM ABERTO'
  }

  if (isMercadoPago) return 'PAGA'

  if (!cobranca) return 'EXCLUÍDA'

  if (pagamento?.dataPagamento || cobranca?.dataPagamento) return 'PAGA'

  if (startOfDay(new Date(cobranca?.dataVencimento)) < startOfToday()) return 'VENCIDA'

  return 'EM ABERTO'
}

const DataNascimentoPicker = ({ disabled: editing }) => {
  const { dadosAluno, loading } = ProgressoStore.useState()
  const dispatch = ProgressoStore.useDispatch()
  const inputRef = useRef<any>(null)

  useEffect(() => {
    let maskInstance = null
    if (inputRef.current) {
      const inputElement = inputRef.current.nativeElement?.querySelector?.('input')
      if (inputElement) {
        maskInstance = Inputmask('99/99/9999').mask(inputElement)
      }
    }

    return () => {
      if (maskInstance) {
        maskInstance.remove()
      }
    }
  }, [])

  const handleChange = (date: any, dateString: string) => {
    dispatch(
      ProgressoStore.actions.updateDadosAluno({
        dataNascimento: date ? date.toISOString() : null,
      })
    )
  }

  return (
    <Form.Item label="Data de Nascimento" layout="vertical" style={{ marginBottom: '0.4rem' }}>
      {loading ? (
        <Skeleton.Input active style={{ width: '100%' }} />
      ) : (
        <DatePicker
          ref={inputRef}
          disabled={editing}
          format="DD/MM/YYYY"
          value={dadosAluno?.dataNascimento ? dayjs(dadosAluno?.dataNascimento) : null}
          onChange={handleChange}
          locale={locale}
          style={{ width: '100%' }}
          placeholder="DD/MM/AAAA"
          inputReadOnly={false}
          allowClear
        />
      )}
    </Form.Item>
  )
}

const DataPrimeiraHabilitacaoPicker = ({ disabled: editing }) => {
  const { dadosAluno, loading } = ProgressoStore.useState()
  const dispatch = ProgressoStore.useDispatch()
  const inputRef = useRef<any>(null)

  useEffect(() => {
    let maskInstance = null
    if (inputRef.current) {
      const inputElement = inputRef.current.nativeElement?.querySelector?.('input')
      if (inputElement) {
        maskInstance = Inputmask('99/99/9999').mask(inputElement)
      }
    }

    return () => {
      if (maskInstance) {
        maskInstance.remove()
      }
    }
  }, [])

  const handleChange = (date: any, dateString: string) => {
    dispatch(
      ProgressoStore.actions.updateDadosAluno({
        dataPrimeiraHabilitacao: date ? date.toISOString() : null,
      })
    )
  }

  return (
    <Form.Item
      label="Data da Primeira Habilitação"
      layout="vertical"
      style={{ marginBottom: '0.4rem' }}
    >
      {loading ? (
        <Skeleton.Input active style={{ width: '100%' }} />
      ) : (
        <DatePicker
          ref={inputRef}
          disabled={editing}
          format="DD/MM/YYYY"
          value={
            dadosAluno?.dataPrimeiraHabilitacao ? dayjs(dadosAluno?.dataPrimeiraHabilitacao) : null
          }
          onChange={handleChange}
          locale={locale}
          style={{ width: '100%' }}
          placeholder="DD/MM/AAAA"
          inputReadOnly={false}
          allowClear
        />
      )}
    </Form.Item>
  )
}

const CategoriaCNHSelect = ({ disabled: editing }) => {
  const { dadosAluno, loading } = ProgressoStore.useState()
  const dispatch = ProgressoStore.useDispatch()

  const categoriasOptions = [
    { value: 'A', label: 'A' },
    { value: 'B', label: 'B' },
    { value: 'AB', label: 'AB' },
    { value: 'AC', label: 'AC' },
    { value: 'AD', label: 'AD' },
    { value: 'AE', label: 'AE' },
    { value: 'C', label: 'C' },
    { value: 'D', label: 'D' },
    { value: 'E', label: 'E' },
  ]

  const handleChange = (value: string) => {
    dispatch(
      ProgressoStore.actions.updateDadosAluno({
        categoriaCnh: value,
      })
    )
  }

  return (
    <Form.Item label="Categoria CNH" layout="vertical" style={{ marginBottom: '0.4rem' }}>
      {loading ? (
        <Skeleton.Input active style={{ width: '100%' }} />
      ) : (
        <Select
          disabled={editing}
          value={dadosAluno?.categoriaCnh || undefined}
          placeholder="Selecione a categoria"
          onChange={handleChange}
          showSearch
          optionFilterProp="children"
          filterOption={(input, option) =>
            (option?.label as string).toUpperCase().includes(input.toUpperCase())
          }
          style={{ width: '100%' }}
          options={categoriasOptions}
        />
      )}
    </Form.Item>
  )
}

const ComboBoxEstados = ({ disabled: editing }) => {
  const { dadosAluno, estados, loading } = ProgressoStore.useState()
  const dispatch = ProgressoStore.useDispatch()

  const handleChange = (value: string) => {
    dispatch(
      ProgressoStore.actions.updateDadosAluno({
        estado: value,
        municipio: '',
      })
    )
  }

  return (
    <Form.Item label="Estado" layout="vertical" style={{ marginBottom: '2rem' }}>
      {loading ? (
        <Skeleton.Input active style={{ width: '100%' }} />
      ) : (
        <Select
          disabled={editing}
          value={dadosAluno?.estado || undefined}
          placeholder="Ex: MG"
          onChange={handleChange}
          showSearch
          style={{ width: '100%' }}
          options={estados?.map((estado) => ({
            value: estado.sigla,
            label: estado.sigla,
          }))}
          filterOption={(input, option) =>
            (option?.label ?? '').toUpperCase().includes(input.toUpperCase())
          }
        />
      )}
    </Form.Item>
  )
}

const ComboBoxMunicipios = ({ disabled: editing }) => {
  const { dadosAluno, municipios, loading } = ProgressoStore.useState()
  const dispatch = ProgressoStore.useDispatch()
  const [inputValue, setInputValue] = useState<string>(dadosAluno?.municipio || '')
  const [estadoValue, setEstadoValue] = useState<string>(dadosAluno?.estado || '')

  useEffect(() => {
    if (estadoValue !== dadosAluno?.estado) {
      setEstadoValue(dadosAluno?.estado)
      setInputValue('')
    }
  }, [dadosAluno?.estado])

  useEffect(() => {
    setInputValue(dadosAluno?.municipio || '')
  }, [dadosAluno?.municipio])

  const options =
    municipios?.map((municipio) => ({
      value: municipio.nome,
      label: municipio.nome,
    })) || []

  const handleSelect = (value: string) => {
    setInputValue(value)
    dispatch(ProgressoStore.actions.updateDadosAluno({ municipio: value }))
  }

  const handleSearch = (value: string) => {
    setInputValue(value)
  }

  const handleClear = () => {
    setInputValue('')
    dispatch(ProgressoStore.actions.updateDadosAluno({ municipio: '' }))
  }

  return (
    <Form.Item label="Município" layout="vertical" style={{ marginBottom: '2rem' }}>
      {loading ? (
        <Skeleton.Input active style={{ width: '100%' }} />
      ) : (
        <AutoComplete
          disabled={editing}
          value={inputValue}
          options={options}
          placeholder="Selecione o município"
          onSelect={handleSelect}
          onSearch={handleSearch}
          filterOption={(inputValue, option) =>
            option!.value.toUpperCase().includes(inputValue.toUpperCase())
          }
          style={{ width: '100%' }}
          allowClear
          onClear={handleClear}
        />
      )}
    </Form.Item>
  )
}

const GerarNovoToken = () => {
  const { token } = ProgressoStore.useState()
  const dispatch = ProgressoStore.useDispatch()

  const handleCopyToClipboard = (text: string) => {
    copyToClipboard(text)
    toast('link para mudança de senha copiado')
  }

  return (
    <DisplayFlex style={{ display: 'flex', justifyContent: 'flex-end' }} className="entire-col">
      {token ? (
        <DisplayFlex style={{ alignItems: 'flex-end' }}>
          <Label
            label="Link para criar senha"
            value={`https://app.newdriver.com.br/criar-senha/${token}`}
          />
          <CopyFilled
            width={18}
            onClick={() =>
              handleCopyToClipboard(`https://app.newdriver.com.br/criar-senha/${token}`)
            }
          />
        </DisplayFlex>
      ) : (
        <Button onClick={() => dispatch(ProgressoStore.thunks.gerarNovoToken())}>
          Gerar novo token
        </Button>
      )}
    </DisplayFlex>
  )
}

const AcoesDetran = () => {
  const dispatch = ProgressoStore.useDispatch()
  const { matricula } = ProgressoStore.useState()

  const showModal = (contentMessage) => {
    return new Promise((resolve, reject) => {
      ModalAntd.confirm({
        title: '',
        content: contentMessage,
        onOk: () => resolve(true),
        onCancel: () => reject(false),
      })
    })
  }

  return (
    <DisplayFlex style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }} className="entire-col">
      <Show condition={matricula?.uf === 'SP'}>
        <Button onClick={() => dispatch(ProgressoStore.thunks.consultarMatricula(matricula))}>
          Consultar Status Matricula
        </Button>
      </Show>
      <Button
        onClick={async () => {
          const confirmar = await showModal(
            `Tem certeza que deseja EXCLUIR a matrícula ${matricula?.id}?`
          )
          if (!confirmar) return
          dispatch(ProgressoStore.thunks.cancelarMatricula(matricula))
        }}
      >
        Cancelar Matrícula
      </Button>
      <Show condition={matricula?.uf === 'SP'}>
        <Button
          onClick={async () => {
            const confirmar = await showModal(
              `Tem certeza que deseja CANCELAR a conclusão da matrícula ${matricula?.id}?`
            )
            if (!confirmar) return
            dispatch(ProgressoStore.thunks.cancelarConclusaoMatricula(matricula))
          }}
        >
          Cancelar Conclusão
        </Button>
      </Show>
    </DisplayFlex>
  )
}

const Sessoes = () => {
  const { sessoesEstudo } = ProgressoStore.useState()

  const columns = [
    { title: 'Id', dataIndex: 'id', key: 'id' },
    {
      title: 'Início',
      dataIndex: 'inicio',
      key: 'inicio',
      render: (datetime) => <FormatedDatetime datetime={datetime} />,
    },
    {
      title: 'Fim',
      dataIndex: 'fim',
      key: 'fim',
      render: (datetime) => <FormatedDatetime datetime={datetime} />,
    },
    {
      title: 'Duração',
      key: 'duracao',
      render: (_, record) => _getDifference(record.inicio, record.fim),
    },
  ]

  return (
    <div>
      <Table columns={columns} dataSource={sessoesEstudo || []} rowKey="id" />
    </div>
  )
}

const Acompanhamento = () => {
  const { progressos } = ProgressoStore.useState()

  const columns = [
    { title: 'Id', dataIndex: 'id', key: 'id' },
    { title: 'Matrícula Id', dataIndex: 'matriculaId', key: 'matriculaId' },
    {
      title: 'Data',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (datetime) => <FormatedDatetime datetime={datetime} />,
    },
    {
      title: 'Tipo',
      key: 'tipo',
      render: (_, record) => (
        <>
          {record.conteudoItem.tipo}
          {record.conteudoItem.tipo === 'VIDEO' ? ` ${+record.porcentagem.toFixed(2)}%` : ''}
        </>
      ),
    },
    {
      title: 'Progresso na página',
      key: 'progressoPagina',
      render: (_, record) => `${record.conteudoItem.ordenacao} / ${record.conteudoItem.total}`,
    },
    {
      title: 'Progresso no módulo',
      key: 'progressoModulo',
      render: (_, record) =>
        `${record.conteudoItem.conteudoPagina.numero} / ${record.conteudoItem.conteudoPagina.total}`,
    },
    {
      title: 'Módulo',
      dataIndex: ['conteudoItem', 'conteudoPagina', 'modulo', 'nome'],
      key: 'modulo',
    },
  ]

  return (
    <div>
      <Table columns={columns} dataSource={progressos || []} rowKey="id" />
    </div>
  )
}

const ValidacoesFaciais = () => {
  const { validacoesFaciais } = ProgressoStore.useState()

  const columns = [
    { title: 'Id', dataIndex: 'id', key: 'id' },
    {
      title: 'Data',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (datetime) => <FormatedDatetime datetime={datetime} />,
    },
    { title: 'Validador', dataIndex: 'servicoValidacao', key: 'servicoValidacao' },
    {
      title: 'Resultado Validação',
      dataIndex: 'resultadoValidacao',
      key: 'resultadoValidacao',
      render: (value) => `${value}%`,
    },
    {
      title: '',
      key: 'action',
      render: (_, vf) => (
        <Modal trigger={<Button>Abrir Foto</Button>}>
          <Modal.Header>Foto validada</Modal.Header>
          <Modal.Content>
            <ModalFoto foto={vf.arquivoFoto} />
          </Modal.Content>
        </Modal>
      ),
    },
  ]

  return (
    <div>
      <Table columns={columns} dataSource={validacoesFaciais || []} rowKey="id" />
    </div>
  )
}

const Avaliacoes = () => {
  const { avaliacoes } = ProgressoStore.useState()

  const columns = [
    { title: 'Id', dataIndex: 'id', key: 'id' },
    { title: 'Matrícula Id', dataIndex: 'matriculaId', key: 'matriculaId' },
    {
      title: 'Data',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (datetime) => <FormatedDatetime datetime={datetime} />,
    },
    { title: 'Pontuação', dataIndex: 'pontuacao', key: 'pontuacao' },
    {
      title: 'Aprovada',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (status === 'APROVADA' ? 'Sim' : 'Não'),
    },
    { title: 'Módulo', dataIndex: 'moduloId', key: 'moduloId' },
  ]

  return (
    <div>
      <Table columns={columns} dataSource={avaliacoes || []} rowKey="id" />
    </div>
  )
}

const EstatisticasDeEstudo = () => {
  const { estatisticasDeEstudo } = ProgressoStore.useState()

  const columns = [
    { title: 'Id', dataIndex: 'id', key: 'id' },
    { title: 'Matrícula Id', dataIndex: 'matriculaId', key: 'matriculaId' },
    { title: 'Módulo', dataIndex: ['modulo', 'nome'], key: 'modulo' },
    { title: 'Página atual', dataIndex: 'paginaNumero', key: 'paginaNumero' },
    {
      title: 'Página mais avançada',
      dataIndex: 'paginaNumeroMaisAvancada',
      key: 'paginaNumeroMaisAvancada',
    },
    {
      title: 'Percentual na página',
      dataIndex: 'percentualProgressoPagina',
      key: 'percentualProgressoPagina',
    },
    {
      title: 'Percentual no módulo',
      dataIndex: 'percentualProgressoGeral',
      key: 'percentualProgressoGeral',
    },
    {
      title: 'Percentual mais avançado',
      dataIndex: 'percentualProgressoMaisAvancado',
      key: 'percentualProgressoMaisAvancado',
    },
    { title: 'Nota na avaliação', dataIndex: 'desempenhoAvaliacao', key: 'desempenhoAvaliacao' },
    {
      title: 'Módulo completo',
      dataIndex: 'moduloCompleto',
      key: 'moduloCompleto',
      render: (value) => (value ? 'Sim' : ''),
    },
  ]

  return <Table columns={columns} dataSource={estatisticasDeEstudo || []} rowKey="id" />
}

const OutrosCursos = () => {
  const { outrosCursos } = ProgressoStore.useState()
  const { getDataFormatada, getDataDistance } = OutrosCursos
  const queryParams = new URLSearchParams(useLocation().search)
  const currentMatriculaId = Number(queryParams.get('matriculaId'))
  const { usuarioId } = useParams<ProgressoParams>()

  const columns = [
    { title: 'Id Matricula', dataIndex: 'id', key: 'id' },
    { title: 'Id Aluno', dataIndex: 'alunoId', key: 'alunoId' },
    { title: 'Curso', dataIndex: ['curso', 'nome'], key: 'curso' },
    {
      title: 'Status',
      key: 'status',
      render: (_, matricula) => <ContainerTag matricula={matricula} />,
    },
    { title: 'Nota', dataIndex: 'nota', key: 'nota' },
    {
      title: 'Data Início',
      dataIndex: 'dataInicioCurso',
      key: 'dataInicioCurso',
      render: (data) => (
        <div>
          <div>{getDataFormatada(data)}</div>
          {data && <div style={{ fontSize: '12px', color: '#666' }}>{getDataDistance(data)}</div>}
        </div>
      ),
    },
    {
      title: 'Data Final',
      dataIndex: 'dataFimCurso',
      key: 'dataFimCurso',
      render: (data) => (
        <div>
          <div>{getDataFormatada(data)}</div>
          {data && <div style={{ fontSize: '12px', color: '#666' }}>{getDataDistance(data)}</div>}
        </div>
      ),
    },
    {
      title: '',
      key: 'action',
      render: (_, matricula) => {
        // Don't show button if it's the same matricula we're currently viewing
        if (matricula?.id === currentMatriculaId) return null
        return <IconSerach matricula={matricula} usuarioId={usuarioId} />
      },
    },
  ]

  return (
    <Table
      columns={columns}
      dataSource={outrosCursos?.matriculas || []}
      rowKey={(record, index) => record?.id || index}
    />
  )
}

OutrosCursos.getDataFormatada = (data: Date) => {
  return data ? format(new Date(data), 'dd/MM/yyyy HH:mm') : ''
}

OutrosCursos.getDataDistance = (data: Date) => {
  return data ? formatDistanceToNow(new Date(data), { addSuffix: true, locale: ptBR }) : ''
}

const ContainerTag = ({ matricula }: { matricula: MatriculaModel }) => {
  const { Tag, catchColorTag, catchStatusNome } = ContainerTag

  return (
    <Tag getTagColor={catchColorTag(matricula?.statusDetran)}>
      <span>{catchStatusNome(matricula?.statusDetran)}</span>
    </Tag>
  )
}
ContainerTag.Tag = styled.div<{ getTagColor: { backgroundColor: string; color: string } }>(
  ({ getTagColor }) => `
  padding: 4px 6px;
  border-radius: 15px;
  background-color: ${getTagColor?.backgroundColor};
  color: ${getTagColor?.color};
  display: grid;
  place-items: center;
  span {
    font-size: 14px;
  }
`
)
ContainerTag.catchColorTag = (statusDetran: string) => {
  if (statusDetran === 'MATRICULADO') return { backgroundColor: '#E0E2FF', color: '#373ECE' }
  if (statusDetran === 'FINALIZADO') return { backgroundColor: '#E7FFD9', color: '#599534' }
  if (statusDetran === 'AGUARDANDO_MATRICULA')
    return { backgroundColor: '#FCEDE2', color: '#FF9722' }
}
ContainerTag.catchStatusNome = (statusDetran: string) => {
  if (statusDetran === 'FINALIZADO') return 'Finalizado'
  if (statusDetran === 'MATRICULADO') return 'Matriculado'
  if (statusDetran === 'AGUARDANDO_MATRICULA') return 'Aguardando Pagamento'
}

const IconSerach = ({ matricula, usuarioId }) => {
  const { Layout } = IconSerach
  const history = useHistory()

  const handleNavigation = () => {
    history.push(
      `/cursos/${matricula?.curso.id}/usuarios/${usuarioId}/acompanhamento?matriculaId=${matricula?.id}`
    )
  }

  return (
    <Layout onClick={handleNavigation}>
      <img src={iconSearch} />
    </Layout>
  )
}
IconSerach.Layout = styled.div`
  display: grid;
  place-items: center;
  border: 2px solid #585858;
  border-radius: 5px;
  padding: 4px;
  cursor: pointer;
  &:hover {
    filter: brightness(0.8);
  }
  img {
    width: 15px;
    filter: invert(100%);
  }
`

const BotaoVoltar = () => {
  const history = useHistory()
  return (
    <Button style={{ margin: '25px auto', maxWidth: 1024 }} onClick={history.goBack} circular>
      <Icon name="arrow left" />
      Voltar
    </Button>
  )
}

const FormatedDatetime = ({ datetime }) => {
  if (!datetime) return null
  return (
    <>
      {format(parseISO(datetime), 'dd/MM/yyyy HH:mm')}
      <span style={{ opacity: 0.5 }}>{format(parseISO(datetime), ':ss')}</span>
    </>
  )
}

const Label = ({ label, value, ...props }) => {
  const { loading } = ProgressoStore.useState()

  return (
    <LabelContainer {...props}>
      <LabelText>{label}</LabelText>
      {loading ? (
        <Skeleton.Input active style={{ width: '100%', height: '22px' }} />
      ) : (
        <ValueText>{value}</ValueText>
      )}
    </LabelContainer>
  )
}

const LabelContainer = styled.div`
  margin-bottom: 0.4rem;
  display: flex;
  flex-direction: column;
`

const LabelText = styled.div`
  align-items: center;
  box-sizing: border-box;
  color: rgba(0, 0, 0, 0.88);
  cursor: default;
  display: inline-flex;
  font-family:
    -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans',
    sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', 'Noto Color Emoji';
  font-size: 14px;
  height: 22px;
  line-height: 22px;
  margin-bottom: 4px;
  max-width: 100%;
  position: relative;
  text-align: start;
`

const ValueText = styled.div`
  font-weight: 600;
  color: rgba(0, 0, 0, 0.88);
  font-size: 14px;
  line-height: 22px;
`

const ModalFoto = ({ foto }: { foto: string }) => {
  const { dadosAluno } = ProgressoStore.useState()
  const cpf = dadosAluno?.cpf.replace(/[-.]/g, '')
  const fotoWithSas = useDecorateBlobWithSas({ blobPath: cpf + '/' + foto })
  return <img style={{ maxWidth: '100%' }} src={fotoWithSas} />
}

function AplicarCupom() {
  const { matricula } = ProgressoStore.useState()
  const dispatch = ProgressoStore.useDispatch()
  const [cupom, setCupom] = React.useState('')
  const [loading, setLoading] = React.useState(false)
  const { usuarioId } = useParams<ProgressoParams>()
  const isEfi = matricula?.pagamento?.provedorPagamento === 'EFI'

  const isPagamentoAberto = (() => {
    if (!matricula) return false

    if (isEfi) {
      if (matricula?.pagamento?.dataPagamento) return false
      if (!matricula?.pagamento?.dataVencimento) return false
      return startOfDay(new Date(matricula?.pagamento?.dataVencimento)) >= startOfToday()
    }

    if (!matricula?.cobranca?.dataVencimento) return false
    return (
      !matricula?.cobranca?.dataPagamento &&
      startOfDay(new Date(matricula?.cobranca?.dataVencimento)) >= startOfToday()
    )
  })()

  async function handleAplicarCupom() {
    setLoading(true)
    try {
      await api.Matriculas.aplicarCupom({
        matriculaId: matricula?.id,
        cupomCodigo: cupom,
      })
      dispatch(ProgressoStore.thunks.getDados(usuarioId, matricula?.id))
    } catch (error) {
      const message = error?.message ?? 'Erro ao aplicar o cupom!'
      toast(message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Show condition={isPagamentoAberto}>
      <div>
        <Label
          label="Cupom"
          className="entire-col"
          value={
            <DisplayFlex style={{ gap: 12 }}>
              <TextFieldInput
                name="nome"
                value={cupom}
                placeholder="Novo cupom"
                onChange={(e) => setCupom(e.target.value)}
              />
              <Button onClick={handleAplicarCupom} loading={loading} style={{ height: 32 }}>
                Aplicar
              </Button>
            </DisplayFlex>
          }
        />
      </div>
    </Show>
  )
}

// const CopyButton = styled(CopyIcon)`
//   fill: darkgray;
//   margin-left: 8px;
//   &:hover {
//     cursor: pointer;
//   }
// `

const formatDate = (date, dateFormat?) => {
  if (!date) return null
  try {
    return format(parseISO(date), dateFormat ?? 'dd/MM/yyyy')
  } catch {
    return date
  }
}

function _getDifference(inicio, fim) {
  const diferenca = differenceInMinutes(parseISO(fim), parseISO(inicio))
  const horas = ~~(diferenca / 60)
  const minutos = diferenca % 60
  const _pluralize = (str, size) => (size === 1 ? str : str + 's')
  const strMinutos = `${('' + minutos).padStart(2, '0')} ${_pluralize('minuto', minutos)}`
  const strHoras = `0${horas} ${_pluralize('hora', horas)}`
  if (!horas && !minutos) return 'Menos de um minuto'
  if (!horas) return strMinutos
  if (horas && !minutos) return strHoras
  return `${strHoras} e ${strMinutos}`
}

function ConfirmarPagamentoManual() {
  const dispatch = ProgressoStore.useDispatch()
  const { matricula, loading } = ProgressoStore.useState()
  const [meioPagamento, setMeioPagamento] = useState<'PIX' | 'BOLETO' | 'CARTAO' | 'ISENTO'>(
    'CARTAO'
  )
  const [provedorPagamento, setProvedorPagamento] = useState<'MERCADO_PAGO' | 'EFI' | 'HYPERPAY'>(
    'MERCADO_PAGO'
  )
  const [descricaoConfirmacao, setDescricaoConfirmacao] = useState('')

  const handleConfirmar = async () => {
    if (!descricaoConfirmacao)
      return toast.error('Informe uma descrição/referência para o pagamento!')
    const isIsencao = meioPagamento === 'ISENTO'
    const result = await ConfirmAlert({
      title: isIsencao
        ? 'Deseja confirmar a isenção de pagamento?'
        : 'Deseja confirmar o pagamento manual?',
      content: '',
    })
    if (!result) return
    dispatch(
      ProgressoStore.thunks.confirmarPagamentoManual({
        matriculaId: matricula?.id,
        meioPagamento,
        provedorPagamento: meioPagamento === 'ISENTO' ? null : provedorPagamento,
        descricaoConfirmacao,
      })
    )
  }

  return (
    <Show condition={!matricula?.pagamento?.dataPagamento}>
      <div className="entire-col" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <Form.Item label="Confirmar Pagamento Manual" layout="vertical" style={{ marginBottom: 0 }}>
          <Select
            value={meioPagamento}
            onChange={setMeioPagamento}
            options={[
              { value: 'CARTAO', label: 'Cartão' },
              { value: 'PIX', label: 'PIX' },
              { value: 'BOLETO', label: 'Boleto' },
              { value: 'ISENTO', label: 'Isento' },
            ]}
            style={{ width: '100%' }}
          />
        </Form.Item>
        <Show condition={meioPagamento !== 'ISENTO'}>
          <Form.Item label="Provedor" layout="vertical" style={{ marginBottom: 0 }}>
            <Select
              value={provedorPagamento}
              onChange={setProvedorPagamento}
              options={[
                { value: 'MERCADO_PAGO', label: 'Mercado Pago' },
                { value: 'EFI', label: 'EFI' },
                { value: 'HYPERPAY', label: 'HyperPay' },
              ]}
              style={{ width: '100%' }}
            />
          </Form.Item>
        </Show>
        <TextFieldInput
          name="descricaoConfirmacao"
          label="Descrição / Referência (auditoria)"
          value={descricaoConfirmacao}
          onChange={(e) => setDescricaoConfirmacao(e.target.value)}
          placeholder="Ex: #123456 - pagamento confirmado por e-mail"
        />
        <Button onClick={handleConfirmar} loading={loading}>
          Confirmar Pagamento
        </Button>
      </div>
    </Show>
  )
}

function Cupom() {
  const { matricula } = ProgressoStore.useState()
  return (
    <Show condition={matricula?.pagamento?.cupomId}>
      <div className="entire-col">
        <Label label="Cupom aplicado" value={matricula?.pagamento?.cupom?.codigo} />
      </div>
    </Show>
  )
}

function ReemitirCobranca() {
  const { matricula } = ProgressoStore.useState()
  const dispatch = ProgressoStore.useDispatch()
  const isEfi = matricula?.pagamento?.provedorPagamento === 'EFI'
  const reemitirCobranca = () => {
    dispatch(ProgressoStore.thunks.reemitirCobranca())
  }
  return (
    <Show
      condition={
        !isEfi &&
        !matricula?.cobranca?.dataPagamento &&
        startOfDay(new Date(matricula?.cobranca?.dataVencimento)) < startOfToday()
      }
    >
      <div className="entire-col">
        <Button style={{ marginLeft: 'auto' }} onClick={reemitirCobranca}>
          Reemitir Cobrança
        </Button>
      </div>
    </Show>
  )
}

const LinksDetran = () => {
  const { matricula } = ProgressoStore.useState()
  const history = useHistory()

  const handleDetranLogs = () => {
    history.push(`/detran-logs?matriculaId=${matricula?.id}`)
  }

  const handleDetranActions = () => {
    history.push(`/detran-actions?matriculaId=${matricula?.id}`)
  }

  return (
    <DisplayFlex style={{ display: 'flex', gap: '6px' }} className="entire-col">
      <Button onClick={handleDetranLogs}>Ver Logs DETRAN</Button>
      <Button onClick={handleDetranActions}>Ações DETRAN</Button>
    </DisplayFlex>
  )
}

function AjustesCobranca() {
  const hasAlteracaoValor = useCanAlterarValor()
  const hasDescontoPix = useCanConfigurarDescontoPix()

  return (
    <Show condition={hasAlteracaoValor || hasDescontoPix}>
      <AjustesCobrancaContainer className="entire-col">
        <AlteracaoValor variant="inline" />
        <PermissaoDescontoPix variant="inline" />
      </AjustesCobrancaContainer>
    </Show>
  )
}

function useCanAlterarValor() {
  const { matricula } = ProgressoStore.useState()
  const isEfi = matricula?.pagamento?.provedorPagamento === 'EFI'

  if (isEfi) {
    return !matricula?.pagamento?.dataPagamento
  }

  if (matricula?.cobranca?.dataPagamento) return false
  if (!matricula?.cobranca?.dataVencimento) return false

  return startOfDay(new Date(matricula.cobranca.dataVencimento)) >= startOfToday()
}

function useCanConfigurarDescontoPix() {
  const { matricula } = ProgressoStore.useState()

  return !!matricula?.pagamento && (matricula?.pixDiscountPercentage ?? 0) > 0
}

function AlteracaoValor({ variant = 'default' }: { variant?: 'default' | 'inline' }) {
  const { matricula } = ProgressoStore.useState()
  const dispatch = ProgressoStore.useDispatch()
  const { usuarioId } = useParams<ProgressoParams>()
  const [valor, setValor] = useState('')
  const [loading, setLoading] = useState(false)
  const shouldShow = useCanAlterarValor()

  async function handleAlterarValor() {
    setLoading(true)
    try {
      if (!valor) {
        return toast.error('Informe um valor')
      }
      // Remove currency formatting before sending to API
      const valorNumerico = valor.replace(/[R$\s.]/g, '').replace(',', '.')
      await api.Matriculas.alterarValor({
        matriculaId: matricula?.id,
        valorNovo: valorNumerico,
      })
      toast.success('Valor alterado com sucesso!')
      dispatch(ProgressoStore.thunks.getDados(usuarioId, matricula?.id))
    } catch (error) {
      const message = error?.message ?? 'Erro ao alterar o valor!'
      toast(message)
    } finally {
      setLoading(false)
    }
  }

  const valueContent = (
    <DisplayFlex style={{ gap: 12 }}>
      <TextFieldInput
        name="valor"
        value={valor}
        placeholder="R$ 0,00"
        onChange={(e) => setValor(e.target.value)}
        mask="currency"
      />
      <Button onClick={handleAlterarValor} loading={loading} style={{ height: 32 }}>
        Alterar
      </Button>
    </DisplayFlex>
  )

  return (
    <Show condition={shouldShow}>
      {variant === 'inline' ? (
        <Label label="Alteração de valor da cobrança" value={valueContent} />
      ) : (
        <Label label="Alteração de valor da cobrança" className="entire-col" value={valueContent} />
      )}
    </Show>
  )
}

function PermissaoDescontoPix({ variant = 'default' }: { variant?: 'default' | 'inline' }) {
  const { matricula } = ProgressoStore.useState()
  const dispatch = ProgressoStore.useDispatch()
  const { usuarioId } = useParams<ProgressoParams>()
  const [loading, setLoading] = useState(false)
  const shouldShow = useCanConfigurarDescontoPix()
  const descontoPixAtivo = matricula?.pagamento?.permiteDescontoPix !== false
  const label = descontoPixAtivo
    ? 'Desconto Pix ativo nesta matrícula'
    : 'Desconto Pix desativado nesta matrícula'

  async function handleChange(e) {
    if (!matricula?.id) return

    const checked = e.target.checked
    setLoading(true)

    try {
      await api.Matriculas.alterarPermissaoDescontoPix({
        matriculaId: matricula.id,
        permiteDescontoPix: checked,
      })
      toast.success('Configuração de desconto Pix atualizada com sucesso!')
      dispatch(ProgressoStore.thunks.getDados(usuarioId, matricula.id))
    } catch (error) {
      const message = error?.message ?? 'Erro ao alterar o desconto Pix!'
      toast(message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Show condition={shouldShow}>
      <div className={variant === 'inline' ? undefined : 'entire-col'}>
        <Checkbox
          label={label}
          checked={descontoPixAtivo}
          disabled={loading || !!matricula?.pagamento?.dataPagamento}
          onChange={handleChange}
        />
      </div>
    </Show>
  )
}
